// Mark delivered — the office records a delivery its driver did not (Umer, 2026-09-29).
//
// Only an order the store has handed to its driver: CONFIRMED (or the old picked-up), a driver on it,
// and its store — unless it was filled from the driver's own car — with no driver-to-driver transfer
// waiting. The stock and history are the driver's delivery exactly (that store, that driver); the
// driver is told who marked it. Migration 0105 refuses the same cases in the database
// (orders.delivered_by, order_marked_delivered_check), so a stale screen cannot get round them.
import { OrderStatus, type Order } from '../types';

export type MarkDeliveredOrder = Pick<Order, 'status' | 'storeId' | 'driverId'> &
  Partial<Pick<Order, 'fulfilledFromDriver' | 'transferPending' | 'orderType'>>;

/** Why this order cannot be marked delivered yet, in the order the office has to fix it — or null. */
export function markDeliveredBlocker(o: MarkDeliveredOrder): string | null {
  if (o.status === OrderStatus.DELIVERED) return 'It is delivered already.';
  if (o.status === OrderStatus.CANCELLED) return 'It is cancelled — put it back first.';
  if (o.status === OrderStatus.RETURNING_TO_STORE) return 'Its driver is returning it to the store — the store confirms the return first.';
  if (o.orderType === 'shipping') return 'A mail order is delivered by Canada Post, not a driver.';
  if (!o.storeId && !o.fulfilledFromDriver) return 'Assign the store first.';
  if (!o.driverId) return 'Assign the driver first.';
  if (o.status !== OrderStatus.CONFIRMED && o.status !== OrderStatus.PICKED_UP) {
    return 'The store has not handed it to the driver yet — the store confirms the handoff (or use Override) first.';
  }
  if (o.transferPending) return 'A driver-to-driver transfer is waiting — finish or cancel it first.';
  return null;
}

/** The database's refusal (0105 / RLS) as a sentence for a toast. */
export function markDeliveredErrorText(message: string): string {
  const m = String(message || '');
  if (/delivered_by/i.test(m) && /does not exist|schema cache|could not find/i.test(m)) {
    return 'Mark delivered needs database update 0105, which has not been applied on this site yet.';
  }
  const refusal = m.match(/order_delivery:\s*(.+)$/);
  if (refusal) return refusal[1];
  return m || 'Could not mark the order delivered.';
}
